import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // `relative overflow-hidden` keeps the click ripple clipped inside the
  // button's rounded bounds. The ripple itself is injected at runtime.
  "relative overflow-hidden inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Spawn a material-style "watery" ripple that grows from the click point,
 * glows, then fades out. The ripple element removes itself once its CSS
 * animation ends, so no React re-render or cleanup state is needed.
 *
 * The `link` variant is skipped (no surface to ripple on).
 */
function spawnRipple(event, variant) {
  if (variant === "link") return;
  const host = event.currentTarget;
  if (!host) return;

  const rect = host.getBoundingClientRect();
  // Diameter = the longest distance from the click point to a corner, so the
  // ripple always covers the whole button regardless of where it's clicked.
  const size = Math.max(rect.width, rect.height) * 2;
  const x = event.clientX - rect.left - size / 2;
  const y = event.clientY - rect.top - size / 2;

  const ripple = document.createElement("span");
  ripple.className = "btn-ripple";
  ripple.style.width = `${size}px`;
  ripple.style.height = `${size}px`;
  ripple.style.left = `${x}px`;
  ripple.style.top = `${y}px`;
  ripple.addEventListener("animationend", () => ripple.remove());

  // Prepend so the ripple paints behind the button's text/icons (which come
  // later in the DOM and therefore stack above it), avoiding any global
  // z-index overrides on button children.
  host.prepend(ripple);
}

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, onPointerDown, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    const handlePointerDown = (event) => {
      // Only ripple on primary (left / touch / pen) presses, and never when disabled.
      if (!props.disabled && event.button === 0) {
        spawnRipple(event, variant)
      }
      onPointerDown?.(event)
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onPointerDown={handlePointerDown}
        {...props} />
    );
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
